///
/// EarthBrowser
///
/// Created by Matt Giger on 9/3/2026
/// Copyright (c) 2026 Geodata Labs
///

import { type EmberDomeAppearance, type EmberDomeBatch, sharedEmberDomeBatch } from "@/earthbrowser/entities/EmberDome/EmberDomeBatch"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { type RenderActionSet } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type color, rgba } from "@stovokor/math/color"
import { cube } from "@stovokor/math/cube"
import { type StovokorContext } from "@stovokor/StovokorContext"

const cNoActionSets: RenderActionSet[] = []

/**
 * A translucent fresnel-shaded sphere marker: nearly transparent face-on
 * with a glowing rim at the silhouette, so the visible glow communicates
 * the clickable hit volume. Optionally pulses for recently active features.
 *
 * The node owns no renderer: when traversed it appends its placement and
 * look to the context's EmberDomeBatch, which draws every dome in one
 * instanced call (see EmberDomeBatch). Its transform is expected to be a
 * uniform scale by the marker radius, as every marker sets it.
 */
export class EmberDomeNode extends NodeBase {
	private readonly batch: EmberDomeBatch
	private readonly look: EmberDomeAppearance

	constructor(context: StovokorContext, color: color = rgba(1, 0.4, 0, 0.85)) {
		super()
		this.batch = sharedEmberDomeBatch(context)
		this.look = { color, opacity: 1, pulse: 0, fill: 0.06, farFill: 1, minAngular: 0 }
	}

	public set color(value: color) {
		this.look.color = value
	}

	/** Overall fade multiplier (0..1) */
	public set opacity(value: number) {
		this.look.opacity = value
	}

	/** Breathing pulse amplitude (0..1) */
	public set pulse(value: number) {
		this.look.pulse = value
	}

	/**
	 * Face-on solidity (0..1): 0 is the airy rim-only fresnel look,
	 * higher values read like a solid colored ball (default 0.06)
	 */
	public set fillAlpha(value: number) {
		this.look.fill = value
	}

	/**
	 * Opacity of the far-mode dot shown when the marker hits its screen-space
	 * size floor (0..1). 1 is a strong solid dot for event markers; lower it
	 * for ambient markers that shouldn't dominate the globe (default 1)
	 */
	public set farFillAlpha(value: number) {
		this.look.farFill = value
	}

	/**
	 * Minimum apparent size in radians of screen-space radius (0 = disabled).
	 * The dome inflates when far away so it never falls below this size,
	 * blending into a solid bright dot for global-scale visibility.
	 */
	public set minAngularSize(value: number) {
		this.look.minAngular = value
	}

	public override render(state: RenderState): RenderActionSet[] {
		if(!this.visible) {
			return cNoActionSets
		}
		// Only the world placement is needed, not the view products a full
		// pushTransform derives; memoized, so a static marker costs nothing
		const model = state.modelOf(this.transform, this.matrixCache)
		this.batch.add(state, model, this.look)
		return cNoActionSets
	}

	/** The unit sphere the batch draws, for parents that size hit volumes from their children */
	public override calculateBoundingBox(): cube {
		return new cube(-1, 1)
	}
}
